/**
 * Sentry initialisation for the browser runtime.
 *
 * DSN + release/environment metadata are fetched at runtime from the
 * backend so the values can be rotated / redeployed without a client
 * rebuild. When absent, Sentry is a graceful no-op so local dev and
 * unconfigured previews keep working.
 *
 * Every captured event carries:
 *   - release      (Sentry's canonical field)   e.g. "1.4.0+2b33b64" or "2b33b64"
 *   - environment  ("production" | "preview" | "development" | ...)
 *   - tag: app.version       raw version if known
 *   - tag: app.commit        full commit SHA if known
 *   - tag: app.commit_short  7-char SHA if known
 *   - tag: app.mode          Vite build mode (dev/prod discriminator)
 */
import * as Sentry from "@sentry/react";
import { getSentryDsn } from "./sentry-config.functions";

let initialised = false;
let initPromise: Promise<void> | null = null;
let cachedDsn: string | null = null;


type SentryMeta = {
  release: string | null;
  version: string | null;
  commit: string | null;
  commitShort: string | null;
  environment: string | null;
};

const meta: SentryMeta = {
  release: null,
  version: null,
  commit: null,
  commitShort: null,
  environment: null,
};

function resolveEnvironment(serverEnv: string | null): string {
  // Prefer the server-provided environment (production/preview/etc.), then
  // fall back to Vite's build mode so dev vs prod is always distinguishable.
  return serverEnv ?? import.meta.env.MODE ?? "unknown";
}

/**
 * Parse a sample-rate env var. Sentry expects a float in [0, 1].
 * Invalid / missing values fall back to `fallback` and log a one-time warning
 * so misconfigured `.env` files don't silently disable tracing or, worse,
 * ship 100 % sampling to production.
 */
const warnedSampleKeys = new Set<string>();
function parseSampleRate(
  rawKey: string,
  raw: string | undefined,
  fallback: number,
): number {
  if (raw === undefined || raw === "") return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n > 1) {
    if (!warnedSampleKeys.has(rawKey)) {
      warnedSampleKeys.add(rawKey);
      // eslint-disable-next-line no-console
      console.warn(
        `[sentry] ${rawKey}="${raw}" is invalid — must be a number in [0, 1]. Falling back to ${fallback}.`,
      );
    }
    return fallback;
  }
  return n;
}

/** Parse a comma-separated list of trace-propagation targets. */
function parsePropagationTargets(raw: string | undefined): (string | RegExp)[] | undefined {
  if (!raw) return undefined;
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return undefined;
  return parts.map((p) => {
    // Support /regex/ syntax so users can supply patterns.
    const m = /^\/(.+)\/([gimsuy]*)$/.exec(p);
    if (m) {
      try { return new RegExp(m[1], m[2]); } catch { return p; }
    }
    return p;
  });
}

export async function initSentry() {
  if (initialised) return;
  if (typeof window === "undefined") return;
  // Runtime guard: React 18 Strict Mode intentionally double-invokes effects
  // in development, so `initSentry()` can be called twice before the first
  // call resolves. Share a single in-flight promise so `Sentry.init` runs
  // exactly once — otherwise we'd install duplicate integrations, register
  // duplicate global handlers, and emit each event twice.
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      const cfg = await getSentryDsn();
      if (!cfg?.dsn) return;
      // A misconfigured secret (e.g. the literal "SENTRY_DSN") makes the SDK
      // log errors on every page load. Only initialise for a real DSN URL.
      if (!/^https:\/\/[^@\s]+@[^/\s]+\/\d+$/.test(cfg.dsn.trim())) {
        console.warn("[sentry] SENTRY_DSN is not a valid DSN — error reporting disabled.");
        return;
      }
      // Re-check after the await in case a parallel caller won the race.
      if (initialised) return;
      cachedDsn = cfg.dsn;
      meta.release = cfg.release ?? null;
      meta.version = cfg.version ?? null;
      meta.commit = cfg.commit ?? null;
      meta.commitShort = cfg.commitShort ?? null;
      meta.environment = resolveEnvironment(cfg.environment ?? null);

      const env = import.meta.env;
      const isProd = (meta.environment ?? env.MODE) === "production";
      const tracesSampleRate = parseSampleRate(
        "VITE_SENTRY_TRACES_SAMPLE_RATE",
        env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined,
        isProd ? 0.1 : 1.0,
      );
      const replaysSessionSampleRate = parseSampleRate(
        "VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE",
        env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE as string | undefined,
        0,
      );
      const replaysOnErrorSampleRate = parseSampleRate(
        "VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE",
        env.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE as string | undefined,
        0.1,
      );
      const profilesSampleRate = parseSampleRate(
        "VITE_SENTRY_PROFILES_SAMPLE_RATE",
        env.VITE_SENTRY_PROFILES_SAMPLE_RATE as string | undefined,
        0,
      );
      const propagationTargets = parsePropagationTargets(
        env.VITE_SENTRY_TRACE_PROPAGATION_TARGETS as string | undefined,
      ) ?? [/^\//, /\/_n\//, /\/api\/public\//];

      Sentry.init({
        dsn: cfg.dsn,
        release: meta.release ?? undefined,
        environment: meta.environment,
        tracesSampleRate,
        replaysSessionSampleRate,
        replaysOnErrorSampleRate,
        profilesSampleRate,
        integrations: [
          Sentry.browserTracingIntegration({}),
          Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
        ],
        tracePropagationTargets: propagationTargets,
        ignoreErrors: [
          /ResizeObserver loop/i,
          /Non-Error promise rejection captured/i,
        ],
      });

      Sentry.setTag("app.mode", import.meta.env.MODE ?? "unknown");
      if (meta.version) Sentry.setTag("app.version", meta.version);
      if (meta.commit) Sentry.setTag("app.commit", meta.commit);
      if (meta.commitShort) Sentry.setTag("app.commit_short", meta.commitShort);
      if (meta.release) Sentry.setTag("app.release", meta.release);
      if (meta.environment) Sentry.setTag("app.environment", meta.environment);

      initialised = true;
    } catch {
      // Sentry init must never break the app. Clear the promise so a later
      // caller can retry (e.g. after a transient network failure fetching
      // the DSN); a successful init flips `initialised` and the top-level
      // guard short-circuits every subsequent call.
      initPromise = null;
    }
  })();
  return initPromise;
}


/** Read-only accessor for callers that want to display the release in UI. */
export function getSentryMeta(): Readonly<SentryMeta> {
  return meta;
}

// ---------------------------------------------------------------------------
// Route change breadcrumbs
// ---------------------------------------------------------------------------
// Called from the router (see RootComponent in src/routes/__root.tsx) each
// time the location changes. Sentry's default `browserTracingIntegration`
// captures history events, but TanStack Router client-side transitions
// don't always fire the same DOM events, so we add an explicit breadcrumb
// per transition — this is what appears in the "Breadcrumbs" section of
// every event.
export function recordRouteChange(
  next: { pathname: string; search?: string; hash?: string },
  prev?: { pathname: string; search?: string; hash?: string } | null,
) {
  if (!cachedDsn) return;
  try {
    const to = `${next.pathname}${next.search ?? ""}${next.hash ?? ""}`;
    const from = prev ? `${prev.pathname}${prev.search ?? ""}${prev.hash ?? ""}` : null;
    Sentry.addBreadcrumb({
      category: "navigation",
      type: "navigation",
      level: "info",
      message: from ? `${from} → ${to}` : to,
      data: {
        from: from ?? undefined,
        to,
        pathname: next.pathname,
        search: next.search || undefined,
        hash: next.hash || undefined,
      },
    });
    // Also expose the current route as a tag so it appears in the event
    // sidebar and is filterable in Issues.
    Sentry.setTag("route.pathname", next.pathname);
  } catch {}
}

// ---------------------------------------------------------------------------
// User context
// ---------------------------------------------------------------------------
// Sentry's `user` field is displayed prominently on every event and is used
// for "users affected" counts. We only send a stable id + non-PII role/flag
// data — never email/name from the auth session unless the caller opts in.
export type SentryUserContext = {
  id: string;
  role?: string | null;
  isAdmin?: boolean | null;
  // Explicit opt-in for PII: only pass when the caller has decided it's
  // acceptable to send email/username to Sentry.
  email?: string | null;
  username?: string | null;
};

export function setSentryUser(user: SentryUserContext | null) {
  if (!cachedDsn) return;
  try {
    if (!user) {
      Sentry.setUser(null);
      Sentry.setTag("user.role", undefined as unknown as string);
      Sentry.setTag("user.is_admin", undefined as unknown as string);
      return;
    }
    Sentry.setUser({
      id: user.id,
      ...(user.email ? { email: user.email } : {}),
      ...(user.username ? { username: user.username } : {}),
    });
    if (user.role != null) Sentry.setTag("user.role", user.role);
    if (user.isAdmin != null) Sentry.setTag("user.is_admin", String(user.isAdmin));
  } catch {}
}

const BOUNDARY_DEDUP_KEY = "woodseer:boundary-captured-refs";
const BOUNDARY_DEDUP_MAX = 50;

/**
 * Read the set of reference ids that have already been reported in the
 * current tab session. Persisted in sessionStorage so a hard reload of a
 * still-broken page (which re-runs componentDidCatch with the same sticky
 * id) does not double-report the same incident to Sentry / the log endpoint.
 */
function readCapturedRefs(): Set<string> {
  try {
    if (typeof window === "undefined") return new Set();
    const raw = window.sessionStorage.getItem(BOUNDARY_DEDUP_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function rememberCapturedRef(referenceId: string) {
  try {
    if (typeof window === "undefined") return;
    const set = readCapturedRefs();
    set.add(referenceId);
    // Bound the list so a runaway page cannot fill sessionStorage.
    const trimmed = Array.from(set).slice(-BOUNDARY_DEDUP_MAX);
    window.sessionStorage.setItem(BOUNDARY_DEDUP_KEY, JSON.stringify(trimmed));
  } catch {}
}

export function captureBoundaryError(
  error: Error,
  info: { componentStack?: string | null; referenceId?: string | null },
  route?: { path?: string; search?: string },
) {
  const referenceId = info.referenceId ?? null;
  // Dedup: emit at most one capture event / Sentry report per reference id
  // per tab session. Reloading a still-broken page re-runs the boundary
  // with the same sticky id — that must not create duplicate incidents.
  const alreadyCaptured = referenceId ? readCapturedRefs().has(referenceId) : false;

  // Emit a lightweight, always-on signal so tests and admin tooling can
  // verify a boundary capture happened even when Sentry has no DSN (local
  // dev, CI). This never contains PII beyond what already appears in the
  // UI reference chip.
  try {
    if (typeof window !== "undefined") {
      const record = {
        referenceId,
        message: error?.message ?? String(error),
        name: error?.name ?? "Error",
        path: route?.path ?? null,
        at: Date.now(),
        reportedToSentry: Boolean(cachedDsn) && !alreadyCaptured,
        deduped: alreadyCaptured,
      };
      (window as unknown as { __lastBoundaryCapture?: typeof record })
        .__lastBoundaryCapture = record;
      if (!alreadyCaptured) {
        window.dispatchEvent(new CustomEvent("boundary:captured", { detail: record }));
      } else {
        // Still expose a distinct signal for observers that want to count
        // re-renders without treating them as new incidents.
        window.dispatchEvent(new CustomEvent("boundary:deduped", { detail: record }));
      }
    }
  } catch {}

  if (alreadyCaptured) return;
  if (referenceId) rememberCapturedRef(referenceId);

  if (!cachedDsn) return;
  try {
    Sentry.withScope((scope) => {
      scope.setTag("source", "global_error_boundary");
      if (info.referenceId) scope.setTag("reference_id", info.referenceId);
      if (route?.path) scope.setTag("route.path", route.path);
      if (route?.search) scope.setExtra("route.search", route.search);
      if (info.componentStack) scope.setExtra("componentStack", info.componentStack);
      // Re-assert release/env at the event level as a belt-and-braces guard
      // (global tags already cover this, but explicit is safer).
      if (meta.release) scope.setTag("app.release", meta.release);
      if (meta.environment) scope.setTag("app.environment", meta.environment);
      if (meta.commitShort) scope.setTag("app.commit_short", meta.commitShort);
      Sentry.captureException(error);
    });
  } catch {}
}


